/**
 * The site's one public origin and the canonical form of its URLs.
 *
 * Pure module (no `window`, no React): the SPA uses it through `<PageHead/>` and the
 * share buttons, and the SEO server (plan W1) will build heads with the same
 * functions, so a canonical can never differ between the two.
 *
 * Canonicals, `og:url`, JSON-LD and share links are always built on `SITE_URL`,
 * never on `window.location.origin` — that made `www.promostork.com` (and any
 * other host serving the bundle) canonicalise to itself.
 */

import { DEFAULT_LOCALE, LOCALES, countryCode, parseLocale, type Locale } from './locales';

// Declared for this module only, not through @types/node, so the rest of the
// browser code still gets a type error if it reaches for `process`.
declare const process: { env: Record<string, string | undefined> };

export const DEFAULT_SITE_URL = 'https://promostork.com';

/** Trim and drop trailing slashes so paths can be appended with a single `/`. */
function normaliseOrigin(value: string): string {
  return value.trim().replace(/\/+$/, '') || DEFAULT_SITE_URL;
}

/**
 * Public origin, without a trailing slash. `||` rather than `??` because deploy
 * env files carry blank keys, and a blank origin must fall back to production.
 * Vite replaces `import.meta.env.SSR` with `false` in the browser bundle, so the
 * `process` branch is dropped there and browser code never touches `process`.
 */
export const SITE_URL: string = normaliseOrigin(
  (import.meta.env.SSR ? process.env.SITE_URL : import.meta.env.VITE_SITE_URL) ||
    DEFAULT_SITE_URL,
);

// ─── Paginated lists (web plan W0.11) ──────────────────────────────────────

/**
 * Items per page of every paginated list: the catalog pages, the list hooks
 * and (plan W1.3) the SEO server, so `?page=N` names the same items everywhere.
 */
export const LIST_PAGE_SIZE = 24;

/**
 * The highest page a list can ask for. The backend's `PaginationOptions.page`
 * is a GraphQL `Int` (32-bit): a larger value fails the whole request.
 */
export const MAX_LIST_PAGE = 2_147_483_647;

/** The `?page=` of a list URL, read by `parseListPage`. */
export interface ListPageParam {
  /** The page to show: 1 when the parameter is absent, blank or invalid. */
  page: number;
  /**
   * False for a value that is not a whole number from 1 to MAX_LIST_PAGE
   * (`0`, `-2`, `2.5`, `1e3`, `abc`): the SPA answers it with `noindex` and
   * "No page N", the SEO server (W1.3) with a 404.
   */
  valid: boolean;
  /** The value as written (`null` without the parameter), for "No page N". */
  raw: string | null;
}

/** Read `?page=N` from a location's query. `?page=03` is page 3; a blank `?page=` is page 1. */
export function parseListPage(search: string): ListPageParam {
  const raw = new URLSearchParams(search).get('page');
  if (raw === null || raw === '') return { page: 1, valid: true, raw };
  if (/^\d+$/.test(raw)) {
    const page = Number(raw);
    if (page >= 1 && page <= MAX_LIST_PAGE) return { page, valid: true, raw };
  }
  return { page: 1, valid: false, raw };
}

/**
 * The last page of a list of `totalRecords` items. An empty list still has a
 * page 1 (its empty state), so any page after it is past the end.
 */
export function lastListPage(totalRecords: number, pageSize = LIST_PAGE_SIZE): number {
  if (!Number.isFinite(totalRecords) || totalRecords <= 0) return 1;
  return Math.max(1, Math.ceil(totalRecords / pageSize));
}

/** Link to page `page` of the list at `path`: page 1 is the bare path, never `?page=1`. */
export function listPageHref(path: string, page: number): string {
  return Number.isSafeInteger(page) && page >= 2 ? `${path}?page=${page}` : path;
}

/** Only `?page=N` with a valid N ≥ 2 survives into a canonical (`?page=1` is the bare path). */
function canonicalPage(search: string): number | null {
  const { page, valid } = parseListPage(search);
  return valid && page >= 2 ? page : null;
}

// ─── Canonical paths ───────────────────────────────────────────────────────

/**
 * First segments of the case-insensitive SEO paths: the catalog lists, the
 * entity pages (`/{type}/{id}`, and the legacy plural `/promotions/{id}`) and
 * the fixed public pages. React Router matches paths case-insensitively, so
 * `/Brands` renders the brand list and must canonicalise to `/brands`.
 * Everything else keeps its case: the token links already in people's inboxes
 * (`/user/confirm/:token`, `/reset-password/:token`,
 * `/user/reset-password/:token`) carry case-sensitive tokens.
 */
const SEO_PATH_ROOTS: ReadonlySet<string> = new Set([
  'brands',
  'promotions',
  'shops',
  'nearby',
  'search',
  'brand',
  'shop',
  'store',
  'promotion',
  'privacy',
  'terms',
  'delete-account',
  'mobile',
  // The company pages (web plan W3.7): `/About` → `/about`.
  'about',
  'contact',
  'how-we-verify',
  'for-businesses',
]);

/**
 * Whether a path (leading `/`, no query) is an SEO path, which is compared and
 * canonicalised in lower case: home, the paths under SEO_PATH_ROOTS and every
 * path under a launched locale (`/pk/en/…`). Token routes never are.
 */
export function isSeoPath(path: string): boolean {
  const first = path.split('/').find((segment) => segment !== '') ?? '';
  return first === '' || SEO_PATH_ROOTS.has(first.toLowerCase()) || splitLocalePath(path) !== null;
}

/**
 * Canonical path for a location: repeated slashes collapsed, no trailing slash
 * (except the root `/`), no fragment, lowercase for SEO paths only (see
 * `isSeoPath`; a token route keeps its case), and every query parameter
 * dropped except `?page=N` for N ≥ 2 (normalised, so `?page=03` → `?page=3`).
 *
 * `pathname` may carry its own `?query` (e.g. a page's `canonicalPath` of
 * `/brands?page=2`); that query is used in place of `search`.
 */
export function canonicalFor(pathname: string, search = ''): string {
  let path = pathname;
  let query = search;

  const hash = path.indexOf('#');
  if (hash !== -1) path = path.slice(0, hash);
  const q = path.indexOf('?');
  if (q !== -1) {
    query = path.slice(q);
    path = path.slice(0, q);
  }

  path = `/${path}`.replace(/\/{2,}/g, '/').replace(/(.)\/+$/, '$1');
  if (isSeoPath(path)) path = path.toLowerCase();
  // Percent-escapes are case-insensitive; RFC 3986 normalises them to upper case.
  path = path.replace(/%[\da-f]{2}/gi, (escape) => escape.toUpperCase());

  const page = canonicalPage(query);
  return page === null ? path : `${path}?page=${page}`;
}

/**
 * Absolute URL on `SITE_URL` for a site path. URLs that already carry an
 * http(s) scheme (images on the media host, a brand's own website) pass through.
 */
export function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * The link handed to the Web Share API or the clipboard for an in-app path:
 * the page's canonical URL on `SITE_URL`, whatever host the sharer is on.
 */
export function shareLink(path: string): string {
  return absoluteUrl(canonicalFor(path));
}

// ─── Locale prefixes and entity paths ──────────────────────────────────────

const LOCALE_PREFIX_ON: ReadonlySet<string> = new Set(['true', '1', 'yes', 'on']);
const LOCALE_PREFIX_OFF: ReadonlySet<string> = new Set(['', 'false', '0', 'no', 'off']);

/**
 * The URL-move switch's value (`VITE_LOCALE_PREFIX` / `LOCALE_PREFIX`), read
 * like the backend's `PUBLIC_LOCALE_PREFIX`: `true`/`1`/`yes`/`on` → on,
 * unset/blank/`false`/`0`/`no`/`off` → off, anything else → `null` (the SEO
 * server refuses to start and the build fails on it; here it counts as off).
 */
export function parseLocalePrefixFlag(value: unknown): boolean | null {
  const text = (typeof value === 'string' ? value : '').trim().toLowerCase();
  if (LOCALE_PREFIX_ON.has(text)) return true;
  if (LOCALE_PREFIX_OFF.has(text)) return false;
  return null;
}

/**
 * Whether the catalog pages (home, lists, nearby, search) live under
 * `/{cc}/{lang}` — the URL move (web plan W2, master D-2). Off by default, so
 * every URL, link, redirect and head stays exactly as before the move.
 *
 * One switch, set in two places that must agree: `VITE_LOCALE_PREFIX` at the
 * SPA's BUILD (inlined into the bundle) and `LOCALE_PREFIX` at the SEO
 * server's RUN time (read from `process.env` when the server bundle loads;
 * Vite replaces `import.meta.env.SSR` like it does for SITE_URL). Both flip in
 * the release that carries the move, together with the backend's
 * `PUBLIC_LOCALE_PREFIX` (docs/seo-server-release.md, "The URL move"). Pages
 * born at their final URL (the sale hubs) use `localePath` directly and
 * ignore this switch.
 */
export const LOCALE_ROUTES_LIVE: boolean =
  parseLocalePrefixFlag(
    import.meta.env.SSR
      ? // `react-router dev` renders on the server too, but loads `.env*` files into
        // `import.meta.env` only (never `process.env`): without the DEV fallback its
        // server HTML had un-prefixed links while the client rendered `/pk/en`
        // (a hydration mismatch). A production bundle never takes the fallback
        // (`import.meta.env.DEV` is false there): the SEO server's `LOCALE_PREFIX`
        // stays the one switch, checked against the SPA build at startup.
        (process.env.LOCALE_PREFIX ?? (import.meta.env.DEV ? import.meta.env.VITE_LOCALE_PREFIX : undefined))
      : import.meta.env.VITE_LOCALE_PREFIX,
  ) === true;

/**
 * The country chooser's switch (web plan W5.7 / MW10; `VITE_LOCALE_CHOOSER` at
 * the SPA's build, `LOCALE_CHOOSER` at the SEO server's run time, read like
 * `LOCALE_PREFIX` above, both off by default). On, with the URL move live and
 * two or more launched locales:
 *  - `/` is the chooser page (200, `x-default`) instead of the 302 to the
 *    default country's home;
 *  - every locale home names it as its `x-default` alternate;
 *  - the "See offers in {Country}?" suggestion can show (client-only).
 * Off, every URL, redirect and head is exactly as before it.
 */
export const LOCALE_CHOOSER_ON: boolean =
  parseLocalePrefixFlag(
    import.meta.env.SSR
      ? (process.env.LOCALE_CHOOSER ?? (import.meta.env.DEV ? import.meta.env.VITE_LOCALE_CHOOSER : undefined))
      : import.meta.env.VITE_LOCALE_CHOOSER,
  ) === true;

/** Whether `/` is the country chooser: its switch on, the URL move live and a second locale launched. */
export function chooserLive(
  chooser: boolean = LOCALE_CHOOSER_ON,
  live: boolean = LOCALE_ROUTES_LIVE,
  launched: number = LOCALES.length,
): boolean {
  return chooser && live && launched >= 2;
}

/** `/pk/en` + `path`: `localePath(pk, '/')` → `/pk/en`, `localePath(pk, '/sale/11-11')` → `/pk/en/sale/11-11`. */
export function localePath(locale: Locale, path = '/'): string {
  const rest = path.startsWith('/') ? path : `/${path}`;
  const prefix = `/${locale.cc}/${locale.lang}`;
  return rest === '/' ? prefix : `${prefix}${rest}`;
}

/**
 * The launched locale a path starts with and the rest of the path
 * (`/pk/en/sale/11-11` → `{ locale: pk-en, path: '/sale/11-11' }`), or `null`
 * for a global (un-prefixed) or unlaunched path.
 */
export function splitLocalePath(pathname: string): { locale: Locale; path: string } | null {
  const match = /^\/([^/?#]+)\/([^/?#]+)(\/[^?#]*)?(?:[?#].*)?$/.exec(pathname);
  if (!match) return null;
  const locale = parseLocale(match[1], match[2]);
  if (!locale) return null;
  return { locale, path: match[3] && match[3] !== '/' ? match[3] : '/' };
}

/**
 * The global (un-prefixed) account pages: they follow the country the visitor
 * was browsing (web plan MW6). The sign-in pages too, so a visitor from
 * another country keeps it there (the header's country) and lands back on
 * its home after signing in.
 */
const ACCOUNT_PATHS: ReadonlySet<string> = new Set([
  '/favorites',
  '/notifications',
  '/notification-settings',
  '/profile',
  '/settings',
  '/login',
  '/register',
  '/verify-otp',
  '/forgot-password',
]);

/** Whether `pathname` is one of the account (or sign-in) pages (case, trailing slash and query ignored). */
export function isAccountPath(pathname: string): boolean {
  return ACCOUNT_PATHS.has(canonicalFor(pathname.split(/[?#]/, 1)[0]).toLowerCase());
}

/**
 * Path of a catalog page (home `/`, `/brands`, `/promotions`, `/nearby`, …)
 * for `locale`: today's un-prefixed path until `LOCALE_ROUTES_LIVE` flips.
 */
export function catalogPath(locale: Locale = DEFAULT_LOCALE, path = '/'): string {
  const rest = path.startsWith('/') ? path : `/${path}`;
  // Another country's page only exists under its prefix (there is no
  // un-prefixed Bahrain), so it is prefixed whatever the switch says.
  return LOCALE_ROUTES_LIVE || locale.cc !== DEFAULT_LOCALE.cc ? localePath(locale, rest) : rest;
}

/**
 * `options.country` for a list or nearby query on a page of `locale` (backend
 * B0.9; `useRequestCountry` in the SPA, the list variables on the SEO
 * server): the locale's country (`PK`) once the URL move is live, else

 * `undefined` for the default country — the option is left out, so requests and cache keys stay
 * as they were before the move (the backend's default is Pakistan either way). Any other
 * country's page always sends its own.
 */
export function requestCountry(
  locale: Locale = DEFAULT_LOCALE,
  live: boolean = LOCALE_ROUTES_LIVE,
): string | undefined {
  // Another country's page always asks for its own country, switch or not:
  // without it the backend would answer with Pakistan's data.
  return live || locale.cc !== DEFAULT_LOCALE.cc ? countryCode(locale) : undefined;
}

export type EntityType = 'brand' | 'shop' | 'store' | 'promotion';

/** Anything with an id, optionally the backend's `urlPath` and Apollo's `__typename`. */
export interface EntityRef {
  id: string;
  urlPath?: string | null;
  __typename?: string;
}

const TYPE_BY_TYPENAME: Readonly<Record<string, EntityType>> = {
  Brand: 'brand',
  Shop: 'shop',
  Store: 'store',
  Promotion: 'promotion',
};

/** Today's id path: `/brand/<id>`, `/promotion/<id>`, … */
export function legacyEntityPath(type: EntityType, id: string): string {
  return `/${type}/${encodeURIComponent(id)}`;
}

/**
 * The site path of an entity. The backend owns URL construction (master D-2):
 * its `urlPath` wins whenever it is present, and the legacy `/{type}/{id}` path
 * is only the fallback for data fetched without it. `type` may be omitted when
 * the object carries Apollo's `__typename`.
 */
export function entityPath(entity: EntityRef, type?: EntityType): string {
  const urlPath = entity.urlPath?.trim();
  // A site path only: never an absolute or protocol-relative URL (`//` or
  // `/\`, which browsers read as `//` in an href).
  if (urlPath && /^\/(?![/\\])/.test(urlPath)) return urlPath;
  const resolved = type ?? TYPE_BY_TYPENAME[entity.__typename ?? ''];
  if (!resolved) {
    throw new TypeError(`entityPath: pass the entity type for "${entity.id}" (no __typename)`);
  }
  return legacyEntityPath(resolved, entity.id);
}
